import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Typography,
  Popper,
  Paper,
} from "@mui/material";
import MainContainer from "theme/MainConatiner";
import {
  LocalOfferOutlined,
  CalendarMonth,
  AccessTime,
  CheckCircleOutline,
  Delete,
  Edit,
  MoreVert,
  Redeem,
  Close,
} from "@mui/icons-material";
import './style.css';
import CustomSearch from "components/Fileds/CustomSearch";
import CustomSelect from "components/Fileds/CustomSelect";
import { CustomPagination } from "components/TableListing/CommonTable";
import CustomTypography from "components/Fileds/CustomTypography";
import HeaderTilteBar from "components/TitleBar/HeaderTilteBar";
import IOSSwitch from "components/Fileds/IOSSwitch";
import OffersModal from "components/Modal/Offers/OffersModal";
import ConfirmationDialog from "components/Modal/ConfirmationDialog/ConfirmationDialog";
import dayjs from "dayjs";
import { currencySymbol } from "utils/constant";
import { DateRangePicker } from 'react-date-range';
import { format } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import enUS from 'date-fns/locale/en-US';

const formatApplicableTo = (val) => {
  if (!val || val === 'all') return 'All Orders';
  const mapping = {
    dine_in: 'Dine In',
    booking: 'Booking',
    delivery: 'Delivery',
    collection_point: 'Collection Point',
    pickup: 'Pickup',
    pos: 'Pickup'
  };
  return val.split(',').map(v => mapping[v.trim()] || v).join(', ');
};

const OffersContainer = ({
  offers = [],
  loading,
  refetch,
  // Filters & State
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
  typeFilter,
  setTypeFilter,
  selectionRange,
  setSelectionRange,
  pickerOpen,
  setPickerOpen,
  anchorEl: datePickerAnchorEl,
  handleButtonClick,
  handleSelect,
  presets,
  statistics,
  // Pagination
  paginationModel,
  setPaginationModel,
  rowCount,
  // Actions
  handleCreateOffer,
  handleEditOffer,
  handleDeleteOffer,
  handleToggleStatus,
  togglingId,
  isMutating,
  isDeleting,
}) => {
  const vendorCurrency = useSelector((state) => state?.posData?.vendor?.currency);
  const currency = currencySymbol(vendorCurrency) || vendorCurrency || '₹';
  const [modalOpen, setModalOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState(null);

  // Anchor element for action menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'expired', label: 'Expired' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'percent', label: 'Percentage' },
    { value: 'fixed', label: 'Flat Amount' }
  ];

  // Stats from backend API
  const statsData = [
    {
      id: 1,
      title: "Total Offers",
      value: statistics?.totalOffers || 0,
      icon: <LocalOfferOutlined sx={{ color: "#475569" }} />,
    },
    {
      id: 2,
      title: "Active",
      value: statistics?.activeOffers || 0,
      icon: <CheckCircleOutline sx={{ color: "#16a34a" }} />,
    },
    {
      id: 4,
      title: "Scheduled",
      value: statistics?.scheduledOffers || 0,
      icon: <CalendarMonth sx={{ color: "#2563eb" }} />,
    },
    {
      id: 5,
      title: "Total Claimed",
      value: statistics?.totalClaimedCount || 0,
      icon: <Redeem sx={{ color: "#9333ea" }} />,
    }
  ];
  const monthStaticRanges = presets?.map(preset => ({
    label: preset.label,
    range: () => ({
      startDate: preset.range.startDate,
      endDate: preset.range.endDate,
    }),
    isSelected: range =>
      range.startDate?.getTime() === preset.range.startDate.getTime() &&
      range.endDate?.getTime() === preset.range.endDate.getTime(),
    color: '#000'
  })) || [];

  const handleClearDate = (e) => {
    e.stopPropagation();
    setSelectionRange({ startDate: null, endDate: null, key: 'selection', color: '#000' });
    setPickerOpen(false);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  // Actions menu handlers
  const handleMenuClick = (event, offer) => {
    setAnchorEl(event.currentTarget);
    setSelectedOffer(offer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedOffer(null);
  };

  const handleEditClick = () => {
    setEditingOffer(selectedOffer);
    setModalOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setOfferToDelete(selectedOffer);
    setConfirmDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    if (offerToDelete) {
      try {
        await handleDeleteOffer(offerToDelete.id);
        setConfirmDialogOpen(false);
        setOfferToDelete(null);
      } catch (err) {
        console.error("Error deleting offer", err);
      }
    }
  };

  // Submit handler for modal
  const handleModalSubmit = async (payload, formikHelpers) => {
    try {
      if (editingOffer) {
        await handleEditOffer({ id: editingOffer.id, ...payload });
      } else {
        await handleCreateOffer(payload);
      }
      setModalOpen(false);
      setEditingOffer(null);
    } catch (err) {
      console.error("Error submitting offer modal", err);
    }
  };

  // Server-side filtering & pagination
  const displayCount = rowCount || 0;
  const paginatedOffers = offers;

  const showNoData = !loading && paginatedOffers.length === 0;

  return (
    <MainContainer>
      <Card className="manage-offers-page">
        <HeaderTilteBar
          title="Offers & Promo Codes"
          isSearch={false}
          isAddVisible={true}
          btnText="Create Offer"
          handleAddBtn={() => {
            setEditingOffer(null);
            setModalOpen(true);
          }}
          rightContent={
            <Box className="stats-wrapper">
              {statsData.map((item) => (
                <Card className="stats-card" key={item.id}>
                  <Avatar className="stats-icon">
                    {item.icon}
                  </Avatar>
                  <Box className="stats-content">
                    <CustomTypography className="stats-value" label={item.value} />
                    <CustomTypography variant="body2" className="stats-title" label={item.title} />
                  </Box>
                </Card>
              ))}
            </Box>
          }
        />

        {/* Filters */}
        <Box className="offers-filter-wrapper">
          <Box className="offers-search-field">
            <CustomSearch
              placeholder="Search by title, code or description..."
              onSearchChange={setSearch}
              searchQuery={search}
              isLoading={loading}
              sx={{
                '& .MuiInputBase-input, & .MuiOutlinedInput-input': {
                  paddingTop: '9.5px',
                  paddingBottom: "12px"
                },
              }}
            />
          </Box>

          <Box className="offers-filter">
            <CustomSelect
              name="status"
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPaginationModel(prev => ({ ...prev, page: 0 }));
              }}
              sx={{ borderRadius: '10px', backgroundColor: '#fff', fontSize: '14px', height: "39.5px", width: "150px" }}
            />

            <CustomSelect
              name="type"
              options={typeOptions}
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPaginationModel(prev => ({ ...prev, page: 0 }));
              }}
              sx={{ borderRadius: '10px', backgroundColor: '#fff', fontSize: '14px', height: "39.5px", width: "150px" }}
            />

            <Box sx={{ position: 'relative' }}>
              <Button
                variant="outlined"
                startIcon={<CalendarMonth />}
                endIcon={selectionRange?.startDate ? (
                  <IconButton
                    size="small"
                    onClick={handleClearDate}
                    sx={{ p: 0.25, ml: 1, color: '#64748b', '&:hover': { color: '#ef4444' } }}
                  >
                    <Close sx={{ fontSize: 16 }} />
                  </IconButton>
                ) : null}
                onClick={handleButtonClick}
                sx={{
                  textTransform: 'none',
                  lineHeight: '1.5',
                  borderRadius: '10px',
                  borderColor: '#cbd5e1',
                  color: '#475569',
                  minWidth: '220px',
                  height: '39.5px',
                  backgroundColor: '#fff',
                  justifyContent: 'flex-start',
                  whiteSpace: 'nowrap',
                  px: '16px',
                  '&:hover': {
                    borderColor: '#94a3b8',
                    backgroundColor: '#f8fafc',
                  },
                  '& .MuiButton-endIcon': {
                    marginLeft: 'auto',
                    marginRight: '-8px',
                  }
                }}
              >
                {selectionRange?.startDate && selectionRange?.endDate
                  ? `${format(selectionRange.startDate, 'MMM dd, yyyy')} - ${format(selectionRange.endDate, 'MMM dd, yyyy')}`
                  : 'Select Date Range'
                }
              </Button>

              <Popper
                open={pickerOpen}
                anchorEl={datePickerAnchorEl}
                placement="bottom-start"
                onClose={() => setPickerOpen(false)}
                disablePortal={false}
                modifiers={[{ name: 'offset', options: { options: [0, 8] } }]}
                style={{ zIndex: 1300 }}
              >
                <Paper elevation={3} sx={{ mt: 1 }}>
                  <DateRangePicker
                    locale={enUS}
                    onChange={handleSelect}
                    months={2}
                    direction="horizontal"
                    showDateDisplay={false}
                    showPreview={false}
                    staticRanges={monthStaticRanges}
                    inputRanges={[]}
                    initialFocusedRange={[0, 0]}
                    moveRangeOnFirstSelection={false}
                    ranges={[selectionRange?.startDate ? selectionRange : {
                      startDate: new Date(),
                      endDate: new Date(),
                      key: 'selection',
                      color: '#000'
                    }]}
                  />
                </Paper>
              </Popper>
            </Box>
          </Box>
        </Box>

        <CustomTypography sx={{ mt: 1, mb: 1, color: "#64748b" }} label={`${displayCount} Offers Found`} />

        {/* Offers List */}
        <Box className="offers-list-body" sx={{ flex: 1, minHeight: '300px' }}>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[1, 2, 3].map((idx) => (
                <Skeleton key={idx} variant="rectangular" height={130} sx={{ borderRadius: "16px" }} />
              ))}
            </Box>
          ) : showNoData ? (
            <Box className="body-wrapper empty-wrapper">
              <LocalOfferOutlined sx={{ color: "#cbd5e1", mb: 1.5, fontSize: 48 }} />
              <CustomTypography className="empty-title" label="No offers found" />
              <Typography className="empty-desc" variant="body2">Try adjusting your filters or search terms.</Typography>
            </Box>
          ) : (
            paginatedOffers.map((offer) => (
              <Card className="offer-card" key={offer.id}>
                {/* Left Type Icon Box */}
                <Box className="offer-type-icon-box">
                  {offer.discount_type === 'percent' ? '%' : currency}
                </Box>

                {/* Main area */}
                <Box className="offer-card-main">
                  <Box className="offer-card-header-row">
                    <CustomTypography className="offer-title" label={offer.title} />
                    <Chip
                      label={offer.computed_status}
                      className={`status-pill ${offer.computed_status}`}
                    />
                    <Chip
                      label={offer.discount_type === 'percent' ? 'Percentage' : 'Flat Discount'}
                      className="type-pill"
                    />
                  </Box>

                  {offer.description && (
                    <CustomTypography className="offer-description" label={offer.description} />
                  )}

                  <Box className="offer-details-row">
                    <Box className="offer-detail-item">
                      <span className="offer-code-badge">{offer.promo_code}</span>
                    </Box>
                    <Box className="offer-detail-item">
                      <CalendarMonth />
                      <span>
                        {dayjs(offer.start_date).format("DD MMM YYYY")} - {dayjs(offer.expiry_date).format("DD MMM YYYY")}
                      </span>
                    </Box>
                    <Box className="offer-detail-item">
                      <span>Min Order: {currency}{parseFloat(offer.min_order_amount).toFixed(2)}</span>
                    </Box>
                    {offer.discount_type === 'percent' && offer.max_discount_amount && (
                      <Box className="offer-detail-item">
                        <span>Max Discount: {currency}{parseFloat(offer.max_discount_amount).toFixed(2)}</span>
                      </Box>
                    )}
                    <Box className="offer-detail-item">
                      <span>Applicable: {formatApplicableTo(offer.applicable_to)}</span>
                    </Box>
                  </Box>

                  {/* Progress limit */}
                  {offer.usage_limit && (
                    <Box className="offer-progress-container">
                      <Typography className="offer-progress-text">
                        Usage: {offer.usage_count || 0} / {offer.usage_limit}
                      </Typography>
                      <Box className="offer-progress-bar">
                        <Box
                          className="offer-progress-fill"
                          sx={{ width: `${Math.min(100, ((offer.usage_count || 0) / offer.usage_limit) * 100)}%` }}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Right Actions */}
                <Box className="offer-card-actions">
                  <IOSSwitch
                    checked={offer.status === 'active'}
                    onChange={() => handleToggleStatus(offer)}
                    disabled={togglingId === offer.id}
                  />
                  <IconButton onClick={(e) => handleMenuClick(e, offer)}>
                    <MoreVert />
                  </IconButton>
                </Box>
              </Card>
            ))
          )}
        </Box>

        {/* Pagination */}
        {displayCount > 0 && (
          <CustomPagination
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            total={displayCount}
          />
        )}

        {/* Actions Dropdown Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleEditClick}>
            <Edit sx={{ mr: 1, fontSize: 18, color: '#1e293b' }} />
            Edit
          </MenuItem>
          <MenuItem onClick={handleDeleteClick} sx={{ color: '#dc2626' }}>
            <Delete sx={{ mr: 1, fontSize: 18, color: '#dc2626' }} />
            Delete
          </MenuItem>
        </Menu>

        {/* Modal Dialog */}
        <OffersModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setEditingOffer(null);
          }}
          initialData={editingOffer}
          onSubmit={handleModalSubmit}
          loading={isMutating}
          currency={currency}
        />

        {/* Delete Confirmation Dialog */}
        <ConfirmationDialog
          open={confirmDialogOpen}
          handleClose={() => {
            setConfirmDialogOpen(false);
            setOfferToDelete(null);
          }}
          handleConfirm={handleConfirmDelete}
          btnText="Delete"
          infoText={`Are you sure you want to delete this offer "${offerToDelete?.title || ''}"?`}
          isLoading={isDeleting}
          action="Delete"
        />
      </Card>
    </MainContainer>
  );
};

export default OffersContainer;
